import type { PresentationSectionV2, QuoteFile } from 'paintscout'
import cloneDeep from 'lodash/cloneDeep'
import type { CategoryPools } from './photo-categories'
import { replaceSectionImages } from './replaceSectionImages'

/**
 * Swaps one v2 **section's** placeholder images for the company's own, handing each of its blocks to
 * replaceSectionImages (which works a level down, on a single block).
 *
 * `usedCount` lets a caller walk several sections without restarting at the first photo in each one
 * — replacePresentationImages threads it across a whole presentation, and a single dropped section
 * starts at 0.
 *
 * Images are consumed in order and looped once exhausted, so a company with three photos still fills
 * a layout with twenty image slots. The loop is guarded on `images.length > 0`: concatenating an
 * empty array onto itself never grows, so an unguarded version spins forever for any presentation
 * with no default images (the same defect replaceSectionImages already carries a guard for).
 *
 * Each block is cloned first, because replaceSectionImages assigns into `fields.cards[]` and
 * `fields.files[]` in place. Without the clone this writes through to whatever the caller passed —
 * for a catalog entry, that is the shared in-memory prefabSections fixture.
 */
export function replaceSectionBlockImages(args: {
  section: PresentationSectionV2
  images: QuoteFile[]
  usedCount?: number
  categoryPools?: CategoryPools
}): { section: PresentationSectionV2; usedCount: number } {
  const { section, images, categoryPools } = args
  let usedCount = args.usedCount ?? 0
  let loopedImages = [...images]

  const blocks = (section.blocks ?? []).map((block) => {
    // loop images if close to end — probably max 6 images per block
    while (images.length > 0 && usedCount + 6 >= loopedImages.length) {
      loopedImages = [...loopedImages, ...images]
    }

    const { section: newBlock, imageCount } = replaceSectionImages({
      section: cloneDeep(block),
      images: loopedImages.slice(usedCount),
      categoryPools
    })
    usedCount += imageCount

    return newBlock
  })

  return { section: { ...section, blocks }, usedCount }
}
